import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, fcfa } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import Header from '../components/Header';
import PaymentBlock from '../components/PaymentBlock';
import { CategoryAlerts } from '../components/CategoryAlerts';
import { PhonePrompt } from '../components/PhonePrompt';
import { FavoriteShops } from '../components/FavoriteShops';
import { OrderTimeline, StatusPill } from '../components/OrderTimeline';
import { DeliveryTracker } from '../components/DeliveryTracker';
import { imgSrc } from '../lib/cart';
import { Trophy, PackageCheck, CreditCard, BellRing, Star, MapPin, Store, ChevronDown, Bell } from 'lucide-react';

export default function BuyerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [openPay, setOpenPay] = useState(null);
  const [openTrack, setOpenTrack] = useState({});

  const load = async () => {
    try {
      const [{ data: o }, { data: n }] = await Promise.all([api.get('/orders/mine'), api.get('/notifications')]);
      setOrders(o); setNotifs(n);
    } catch (e) {}
  };
  useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, []);
  useEffect(() => { setOpenTrack((s) => { const n = { ...s }; orders.forEach((o) => { if (o.status === 'shipped' && n[o.order_id] === undefined) n[o.order_id] = true; }); return n; }); }, [orders]);

  if (!user) return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">{t('login')}</div></div>;
  const active = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));
  const done = orders.filter((o) => ['delivered', 'cancelled'].includes(o.status));

  const Card = ({ o }) => {
    const thumb = o.items?.[0]?.image_url || '';
    return (
      <article data-testid={`order-${o.order_id}`} className={`rounded-3xl p-5 sm:p-6 space-y-5 reveal ${o.status === 'shipped' ? 'glass-gold' : 'glass'}`}>
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-2xl overflow-hidden bg-[#0b1120] ring-1 ring-white/10 shrink-0 grid place-items-center">{thumb ? <img src={imgSrc(thumb)} alt="" className="h-full w-full object-cover" /> : <PackageCheck className="h-6 w-6 text-slate-600" />}</div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={o.status} testid={`order-status-${o.order_id}`} />
              {o.type === 'shop' && <span className="text-[10px] uppercase tracking-widest text-slate-500">Boutique</span>}
              {o.type !== 'shop' && <span className="text-[10px] uppercase tracking-widest text-slate-500">Enchère</span>}
            </div>
            <div className="font-display font-bold text-lg mt-1.5 truncate">{o.product_name}</div>
            <div className="text-xs text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
              <span>{new Date(o.created_at).toLocaleString()}</span>
              {o.seller_name && <Link to={`/seller/${o.seller_id}`} className="inline-flex items-center gap-1 hover:text-[#F5D98B]"><Store className="h-3 w-3" />{o.seller_name}</Link>}
              {o.delivery?.address && <span className="inline-flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{o.delivery.address}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="eyebrow">Total</div>
            <div className="font-mono-num text-xl font-extrabold gold-text">{fcfa(o.amount)}</div>
            {o.delivery_fee > 0 && <div className="text-[10px] text-slate-500">dont livraison {fcfa(o.delivery_fee)}</div>}
          </div>
        </div>

        <OrderTimeline status={o.status} testid={`order-timeline-${o.order_id}`} />

        {o.type === 'shop' && (
          <div className="rounded-2xl bg-[#0b1120]/70 border border-white/5 p-3 text-sm space-y-1" data-testid={`order-breakdown-${o.order_id}`}>
            <div className="flex justify-between text-slate-400"><span>Articles</span><span className="font-mono-num">{fcfa(o.subtotal ?? o.amount)}</span></div>
            <div className="flex justify-between items-center gap-3">
              <span className="text-slate-400">Livraison{o.delivery?.city ? <span className="text-slate-500"> · {o.delivery.city}{o.delivery.district ? `, ${o.delivery.district}` : ''}</span> : null}</span>
              <span data-testid={`order-fee-status-${o.order_id}`} className={`font-mono-num ${o.delivery_fee_status === 'to_confirm' ? 'text-[#F5D98B] text-xs' : o.delivery_fee_status === 'proposed' ? 'text-amber-300' : 'text-emerald-300'}`}>
                {o.delivery_fee_status === 'to_confirm' ? 'À confirmer par le vendeur' : o.delivery_fee ? fcfa(o.delivery_fee) : 'Offerte'}{o.delivery_fee_status === 'proposed' ? ' · à accepter' : ''}
              </span>
            </div>
            {o.delivery_fee_note && <div className="text-xs text-slate-400">{o.delivery_fee_note}</div>}
            <div className="flex justify-between items-center pt-1 border-t border-white/5"><span className="font-bold">Total</span><span className="font-mono-num font-extrabold gold-text">{fcfa(o.amount)}</span></div>
            {o.status === 'won' && o.delivery_fee_status === 'proposed' && (
              <button data-testid={`accept-fee-${o.order_id}`} onClick={async () => { try { await api.post(`/orders/${o.order_id}/delivery-fee/accept`); toast.success('Montant accepté — vous pouvez payer'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } }}
                className="mt-2 w-full py-2.5 rounded-full btn-gold font-bold text-sm">Accepter le total de {fcfa(o.amount)}</button>
            )}
          </div>
        )}

        {o.status === 'shipped' && (
          <div className="space-y-3">
            <button onClick={() => setOpenTrack((s) => ({ ...s, [o.order_id]: !s[o.order_id] }))} data-testid={`toggle-track-${o.order_id}`} className="w-full flex items-center justify-between text-sm font-bold text-[#F5D98B]">
              <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />Suivi du livreur en temps réel</span><ChevronDown className={`h-4 w-4 transition-transform ${openTrack[o.order_id] ? 'rotate-180' : ''}`} />
            </button>
            {openTrack[o.order_id] && <DeliveryTracker orderId={o.order_id} />}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {o.type === 'shop' && o.status === 'won' && (
            <button data-testid={`cancel-order-${o.order_id}`} onClick={async () => { if (!window.confirm('Annuler cette commande ?')) return; try { await api.post(`/orders/${o.order_id}/cancel`); toast.success('Commande annulée'); load(); } catch { toast.error('Erreur'); } }}
              className="text-xs text-slate-400 hover:text-rose-400 px-3 py-2 transition-colors">Annuler</button>
          )}
          {o.status === 'won' && o.type === 'shop' && ['to_confirm', 'proposed'].includes(o.delivery_fee_status) && (
            <span className="text-xs text-slate-400" data-testid={`pay-wait-${o.order_id}`}>{o.delivery_fee_status === 'to_confirm' ? 'Le vendeur confirme les frais de livraison, vous pourrez ensuite payer.' : 'Acceptez le total ci-dessus pour payer.'}</span>
          )}
          {o.status === 'won' && !(o.type === 'shop' && ['to_confirm', 'proposed'].includes(o.delivery_fee_status)) && (
            <button data-testid={`pay-btn-${o.order_id}`} onClick={() => setOpenPay(openPay === o.order_id ? null : o.order_id)}
              className="px-5 py-2.5 rounded-full btn-gold font-bold text-sm inline-flex items-center gap-2"><CreditCard className="h-4 w-4" />{t('pay_now')}</button>
          )}
          {o.status === 'payment_submitted' && (
            <button data-testid={`ping-btn-${o.order_id}`} onClick={async () => { try { await api.post(`/orders/${o.order_id}/ping`); toast.success('Vendeur notifié'); } catch { toast.error('Error'); } }}
              className="px-4 py-2.5 rounded-full btn-ghost-gold font-bold text-xs inline-flex items-center gap-2"><BellRing className="h-3.5 w-3.5" />Relancer le vendeur</button>
          )}
          {o.status === 'delivered' && (
            <>
              {(o.items?.length ? o.items : [{ product_id: o.product_id, name: o.product_name }]).filter((it) => it.product_id).map((it) => (
                <Link key={it.product_id} to={`/product/${it.product_id}#avis`} data-testid={`review-product-${o.order_id}-${it.product_id}`}
                  className="px-4 py-2.5 rounded-full btn-gold font-bold text-xs inline-flex items-center gap-2"><Star className="h-3.5 w-3.5" />Noter {o.items?.length > 1 ? it.name : 'le produit'}</Link>
              ))}
              <Link to={`/seller/${o.seller_id}`} data-testid={`review-link-${o.order_id}`} className="px-4 py-2.5 rounded-full btn-ghost-gold font-bold text-xs inline-flex items-center gap-2"><Star className="h-3.5 w-3.5" />Noter le vendeur</Link>
            </>
          )}
        </div>
        {openPay === o.order_id && <PaymentBlock order={o} onSubmitted={() => { setOpenPay(null); load(); }} />}
      </article>
    );
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_340px] gap-8">
        <div>
          <div className="eyebrow">Espace acheteur</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-1 flex items-center gap-3"><Trophy className="h-9 w-9 text-[#E8B84A]" />Mes achats</h1>
          <div className="gold-line mt-5 mb-8" />
          <div className="space-y-5">
            {orders.length === 0 && <div className="glass rounded-3xl p-12 text-center text-slate-400" data-testid="orders-empty">Aucune commande pour le moment. <Link to="/shop" className="text-[#F5D98B] font-bold">Voir la boutique</Link></div>}
            {active.map((o) => <Card key={o.order_id} o={o} />)}
            {done.length > 0 && <div className="eyebrow pt-4">Historique</div>}
            {done.map((o) => <Card key={o.order_id} o={o} />)}
          </div>
        </div>

        <aside className="space-y-6">
          <PhonePrompt hasPendingWin={orders.some((o) => o.status === 'won')} />
          <FavoriteShops />
          <CategoryAlerts />
          <div className="glass rounded-3xl p-5">
            <h2 className="font-display text-lg font-bold flex items-center gap-2"><Bell className="h-4 w-4 text-[#E8B84A]" />{t('notifications')}</h2>
            <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {notifs.length === 0 && <div className="text-slate-500 text-sm">{t('no_notifications')}</div>}
              {notifs.map((n) => (
                <div key={n.notif_id} className="p-3 rounded-2xl bg-[#0b1120]/70 border border-white/5 text-sm text-slate-200">
                  <div>{n.message}</div>
                  {n.live_id && <Link to={`/live/${n.live_id}`} className="text-rose-400 text-xs font-bold hover:underline">Rejoindre le live →</Link>}
                  <div className="text-[10px] text-slate-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
